import { useContext, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Logo } from '@rossum/ui';
import {
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { useLogin } from '../../business/auth/useLogin';
import { useSignInWithAuthCodeFromUrl } from '../../business/auth/useSignInWithAuthCodeFromUrl';
import Container, { PADDING_TOP } from '../../components/UI/Container';
import { enterLogin } from '../../redux/modules/ui/actions';
import { HEADER_HEIGHT } from './components/AnimatedHeader';
import Form from './components/Form';
import Options from './components/Options';
import { LoginAnimationContext } from './LoginAnimationContext';
import { useAutoSsoLogin } from './useAutoSsoLogin';
import { useDomainSettings } from './useDomainSettings';

const LOGO_SIZE = 30;

const Login = () => {
  const intl = useIntl();

  const { isSigningIn } = useSignInWithAuthCodeFromUrl();
  const { setLoginUnmounted, setAnimationState, setTitle } = useContext(
    LoginAnimationContext
  );

  const { hostname } = new URL(window.location.origin);
  const autoSsoState = useAutoSsoLogin({ domain: hostname });

  // The login form must not paint before we know whether this domain allows
  // direct login. On a failed fetch `data` stays undefined and we fall through
  // to the form, so a network blip can't lock everyone out.
  //
  // `!isFetched` matters: `Options` observes the same query, so once the form is
  // on screen a refetch-on-mount would flip the status back to 'loading' and
  // swap the form for the loader, unmounting the observer that triggered it —
  // an endless loop. Once the query has settled the loader never comes back.
  const domainSettings = useDomainSettings({ domain: hostname });
  const isLoadingDomainSettings =
    domainSettings.status === 'loading' && !domainSettings.isFetched;
  const isLoginPageDisabled = !!domainSettings.data?.disableLoginPage;

  const loginPhase = intl.formatMessage({
    id: 'containers.login.welcome',
  });

  const { mutate: login, isLoading, error } = useLogin();

  const dispatch = useDispatch();

  // temporary replacement of onEnter action dispatching on route
  useEffect(() => {
    dispatch(enterLogin());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      setAnimationState('ready');
    }
  }, [error, setAnimationState]);

  useEffect(() => {
    if (isLoading) {
      setAnimationState('loading');
    }
  }, [isLoading, setAnimationState]);

  useEffect(() => {
    setTitle(loginPhase);
  }, [loginPhase, setTitle]);

  useEffect(() => {
    setLoginUnmounted(false);
    const animationState =
      autoSsoState.type === 'inactive' &&
      !isLoadingDomainSettings &&
      !isLoginPageDisabled
        ? 'ready'
        : 'hidden';
    setAnimationState(animationState);

    return () => {
      setLoginUnmounted(true);
    };
  }, [
    setAnimationState,
    setLoginUnmounted,
    autoSsoState.type,
    isLoadingDomainSettings,
    isLoginPageDisabled,
  ]);

  return (
    <Container
      dataPageTitle="login"
      customTopOffset={PADDING_TOP + HEADER_HEIGHT}
    >
      <Helmet
        meta={[
          {
            name: 'description',
            content:
              'Log into your Rossum account here. Rossum creates a world free of manual data entry by doing the tedious work for you, so you can focus on other tasks.',
          },
        ]}
      />
      {isSigningIn ||
      autoSsoState.type === 'loadingSsoProviders' ||
      autoSsoState.type === 'waitingForSsoConfirmation' ? (
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          {autoSsoState.type === 'loadingSsoProviders' ? null : (
            <Typography>
              {intl.formatMessage({
                id: 'containers.login.signingIn',
              })}
            </Typography>
          )}
        </Stack>
      ) : autoSsoState.type === 'button' ? (
        <Button variant="outlined" onClick={autoSsoState.onClick}>
          {intl.formatMessage(
            { id: 'components.authProviders.unknownProvider' },
            { authProvider: autoSsoState.providerName }
          )}
        </Button>
      ) : isLoadingDomainSettings ? (
        <Stack alignItems="center">
          <CircularProgress data-cy="login-loader" />
        </Stack>
      ) : isLoginPageDisabled ? (
        <Stack alignItems="center" spacing={2}>
          <Logo size={LOGO_SIZE} />
          <Typography
            variant="h6"
            data-cy="login-disabled-message"
            align="center"
          >
            {intl.formatMessage({ id: 'containers.login.disabled' })}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={4} alignItems="center" sx={{ width: '100%', pt: 5 }}>
          <Form login={login} />
          <Options domain={hostname} />
        </Stack>
      )}
    </Container>
  );
};

export default Login;
